import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MerchantHeader from '../components/MerchantHeader';
import { toast } from 'sonner';
import { 
  Key, Copy, RefreshCw, Trash2, Plus, Eye, EyeOff, 
  Code2, Terminal, BookOpen, ExternalLink, CheckCircle,
  AlertTriangle, Shield, Zap, BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function DeveloperAPIPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [brandInfo, setBrandInfo] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDescription, setNewKeyDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState(null);

  const getToken = () => sessionStorage.getItem('session_token');

  const loadUserProfile = useCallback(async () => {
    const token = getToken();
    if (!token) {
      navigate('/login');
      return null;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to load profile');
      
      const data = await response.json();
      setUser(data.member);
      return data.member;
    } catch (error) {
      console.error('Profile error:', error);
      navigate('/login');
      return null;
    }
  }, [navigate]);

  const loadBrandInfo = useCallback(async (brandId) => {
    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/api/admin/brands/${brandId}/info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBrandInfo(data);
      }
    } catch (error) {
      console.error('Brand info error:', error);
    }
  }, []);

  const loadApiKeys = useCallback(async (brandId) => {
    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/api/admin/brands/${brandId}/api-keys`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data.keys || []);
      }
    } catch (error) {
      console.error('API keys error:', error);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const profile = await loadUserProfile();
      if (profile) {
        // Derive brand_id from merchant_id or use existing
        const brandId = profile.brand_id || `brand_${profile.merchant_id?.substring(0, 8)}`;
        await Promise.all([
          loadBrandInfo(brandId),
          loadApiKeys(brandId)
        ]);
      }
      setLoading(false);
    };
    init();
  }, [loadUserProfile, loadBrandInfo, loadApiKeys]);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a key name');
      return;
    }

    setCreating(true);
    const token = getToken();
    const brandId = brandInfo?.brand_id;

    try {
      const response = await fetch(`${API_URL}/api/admin/brands/${brandId}/api-keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newKeyName,
          description: newKeyDescription || null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create API key');
      }

      const data = await response.json();
      setNewKey(data);
      setShowCreateModal(false);
      setShowKeyModal(true);
      setNewKeyName('');
      setNewKeyDescription('');
      
      // Refresh keys list
      await loadApiKeys(brandId);
      toast.success('API key created successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRotateKey = async (keyId) => {
    const token = getToken();
    const brandId = brandInfo?.brand_id;

    try {
      const response = await fetch(`${API_URL}/api/admin/brands/${brandId}/api-keys/${keyId}/rotate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to rotate API key');
      }

      const data = await response.json();
      setNewKey(data);
      setShowKeyModal(true);
      
      await loadApiKeys(brandId);
      toast.success('API key rotated. Old key will stop working in 24 hours.');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    const token = getToken();
    const brandId = brandInfo?.brand_id;

    try {
      const response = await fetch(`${API_URL}/api/admin/brands/${brandId}/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to revoke API key');
      }

      await loadApiKeys(brandId);
      toast.success('API key revoked');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const copyToClipboard = async (text, snippetId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSnippet(snippetId);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedSnippet(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('session_token');
    navigate('/login');
  };

  // Code snippets
  const brandId = brandInfo?.brand_id || 'YOUR_BRAND_ID';
  
  const loaderSnippet = `<!-- Commerce Index Loader -->
<script>
  (function(w, d, s, src, name) {
    if (w[name]) { return; }
    var ci = w[name] = function() { ci.q.push(arguments); };
    ci.q = [];
    var el = d.createElement(s);
    el.async = true;
    el.src = src;
    var firstScript = d.getElementsByTagName(s)[0];
    firstScript.parentNode.insertBefore(el, firstScript);
  })(window, document, 'script', 'https://cdn.commerceindex.com/ci.js', 'ci');

  ci('init', { brandId: '${brandId}' });
</script>`;

  const trackViewSnippet = `<!-- On product detail pages -->
<script>
  ci('trackView', {
    productId: '{{ YOUR_PRODUCT_ID }}',
    productSku: '{{ YOUR_PRODUCT_SKU }}',
    productTitle: '{{ YOUR_PRODUCT_TITLE }}',
    productUrl: window.location.href
  });
</script>`;

  const trackPurchaseSnippet = `<!-- On order confirmation page -->
<script>
  ci('trackPurchase', {
    orderId: '{{ YOUR_ORDER_ID }}',
    currency: 'USD',
    items: [
      {
        productId: '{{ PRODUCT_ID }}',
        productSku: '{{ PRODUCT_SKU }}',
        productTitle: '{{ PRODUCT_TITLE }}',
        quantity: 1,
        value: 49.99
      }
    ]
  });
</script>`;

  const brandSignalsCurl = `curl -X GET "https://api.commerceindex.com/v1/signals/brand?brand_id=${brandId}&window=30d" \\
  -H "X-CI-Api-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json"`;

  const productSignalsCurl = `curl -X GET "https://api.commerceindex.com/v1/signals/product?brand_id=${brandId}&product_id=prod_123&window=7d" \\
  -H "X-CI-Api-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json"`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B1120]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <MerchantHeader user={user} onLogout={handleLogout} />
      
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">API & Signals</h1>
          <p className="text-gray-400">
            Issue API keys, send events, and read Commerce Index Signals for your brand.
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-[#0D1526] border-[#1E3A5F]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <Code2 className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-medium">Send Events</p>
                <p className="text-sm text-gray-400">Track views & purchases</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0D1526] border-[#1E3A5F]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-green-500/10 rounded-lg">
                <Key className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-white font-medium">API Keys</p>
                <p className="text-sm text-gray-400">{apiKeys.filter(k => k.status === 'active').length} active</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0D1526] border-[#1E3A5F]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-purple-500/10 rounded-lg">
                <BarChart3 className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-white font-medium">Events This Month</p>
                <p className="text-sm text-gray-400">{brandInfo?.events_this_month?.toLocaleString() || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Brand Info */}
        <Card className="bg-[#0D1526] border-[#1E3A5F] mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-400" />
              Brand Info
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label className="text-gray-400 text-sm">Brand ID</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-white bg-[#1E293B] px-3 py-2 rounded font-mono text-sm flex-1">
                    {brandId}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(brandId, 'brand-id')}
                    data-testid="copy-brand-id"
                  >
                    {copiedSnippet === 'brand-id' ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-400" />
                    )}
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-gray-400 text-sm">Environment</Label>
                <div className="mt-1">
                  <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30">
                    Production
                  </Badge>
                </div>
              </div>
              <div>
                <Label className="text-gray-400 text-sm">Brand Name</Label>
                <p className="text-white mt-1">{brandInfo?.brand_name || user?.business_name || 'Your Brand'}</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm mt-4">
              Your brand_id is used when sending events and requesting brand-level signals.
            </p>
          </CardContent>
        </Card>

        {/* API Keys Section */}
        <Card className="bg-[#0D1526] border-[#1E3A5F] mb-8">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <Key className="w-5 h-5 text-yellow-400" />
                API Keys
              </CardTitle>
              <CardDescription className="text-gray-400 mt-1">
                Use API keys to authenticate server-to-server and browser-based integrations.
                Keep your keys secret and rotate them if exposed.
              </CardDescription>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="create-api-key-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create API Key
            </Button>
          </CardHeader>
          <CardContent>
            {apiKeys.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-[#1E3A5F] rounded-lg">
                <Key className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 mb-4">No API keys yet</p>
                <Button
                  onClick={() => setShowCreateModal(true)}
                  variant="outline"
                  className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                >
                  Create your first API key
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#1E3A5F]">
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Name</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Key</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Created</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Last Used</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Status</th>
                      <th className="text-right py-3 px-4 text-gray-400 font-medium text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiKeys.map((key) => (
                      <tr key={key.id} className="border-b border-[#1E3A5F]/50 hover:bg-[#1E293B]/30">
                        <td className="py-3 px-4">
                          <span className="text-white font-medium">{key.name}</span>
                          {key.description && (
                            <p className="text-gray-500 text-sm">{key.description}</p>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <code className="text-gray-400 font-mono text-sm">{key.key_prefix}</code>
                        </td>
                        <td className="py-3 px-4 text-gray-400 text-sm">
                          {new Date(key.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 text-gray-400 text-sm">
                          {key.last_used_at 
                            ? new Date(key.last_used_at).toLocaleDateString()
                            : 'Never'
                          }
                        </td>
                        <td className="py-3 px-4">
                          <Badge
                            variant="outline"
                            className={
                              key.status === 'active' 
                                ? 'bg-green-500/10 text-green-400 border-green-500/30'
                                : key.status === 'revoking'
                                ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'
                                : 'bg-red-500/10 text-red-400 border-red-500/30'
                            }
                          >
                            {key.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          {key.status === 'active' && (
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRotateKey(key.id)}
                                className="text-gray-400 hover:text-white"
                                title="Rotate key"
                                data-testid={`rotate-key-${key.id}`}
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRevokeKey(key.id)}
                                className="text-gray-400 hover:text-red-400"
                                title="Revoke key"
                                data-testid={`revoke-key-${key.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Integration Tabs */}
        <Tabs defaultValue="events" className="space-y-6">
          <TabsList className="bg-[#0D1526] border border-[#1E3A5F]">
            <TabsTrigger value="events" className="data-[state=active]:bg-blue-600">
              <Code2 className="w-4 h-4 mr-2" />
              Web Integration
            </TabsTrigger>
            <TabsTrigger value="signals" className="data-[state=active]:bg-blue-600">
              <BarChart3 className="w-4 h-4 mr-2" />
              Signals API
            </TabsTrigger>
          </TabsList>

          {/* Events Tab */}
          <TabsContent value="events">
            <Card className="bg-[#0D1526] border-[#1E3A5F]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  Send View & Purchase Events (CI Proof)
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Use this JavaScript snippet on your product detail pages and order confirmation page
                  to send view and purchase events to Commerce Index. These events power live Proof
                  (&quot;X viewed / Y purchased&quot;) and CI Scores and Signals.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Loader Snippet */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-white font-medium">1. Add the CI Loader (once per page)</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(loaderSnippet, 'loader')}
                      data-testid="copy-loader"
                    >
                      {copiedSnippet === 'loader' ? (
                        <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      Copy
                    </Button>
                  </div>
                  <pre className="bg-[#1E293B] p-4 rounded-lg overflow-x-auto text-sm text-gray-300 font-mono">
                    {loaderSnippet}
                  </pre>
                </div>

                {/* Track View Snippet */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-white font-medium">2. Track Product Views (on PDPs)</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(trackViewSnippet, 'trackView')}
                      data-testid="copy-track-view"
                    >
                      {copiedSnippet === 'trackView' ? (
                        <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      Copy
                    </Button>
                  </div>
                  <pre className="bg-[#1E293B] p-4 rounded-lg overflow-x-auto text-sm text-gray-300 font-mono">
                    {trackViewSnippet}
                  </pre>
                </div>

                {/* Track Purchase Snippet */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-white font-medium">3. Track Purchases (on order confirmation)</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(trackPurchaseSnippet, 'trackPurchase')}
                      data-testid="copy-track-purchase"
                    >
                      {copiedSnippet === 'trackPurchase' ? (
                        <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      Copy
                    </Button>
                  </div>
                  <pre className="bg-[#1E293B] p-4 rounded-lg overflow-x-auto text-sm text-gray-300 font-mono">
                    {trackPurchaseSnippet}
                  </pre>
                </div>

                {/* Links to docs */}
                <div className="flex items-center gap-4 pt-4 border-t border-[#1E3A5F]">
                  <BookOpen className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-400">See full integration examples:</span>
                  <a href="/docs/examples" className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                    Shopify <ExternalLink className="w-3 h-3" />
                  </a>
                  <a href="/docs/examples" className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                    Next.js/React <ExternalLink className="w-3 h-3" />
                  </a>
                  <a href="/docs/examples" className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                    Server-side <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Signals Tab */}
          <TabsContent value="signals">
            <Card className="bg-[#0D1526] border-[#1E3A5F]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                  Query Commerce Signals
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Once your events are flowing, you can query CI Signals to get structured insights
                  about your brand and products.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Brand Signals */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-white font-medium">Brand Signals Example</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(brandSignalsCurl, 'brandSignals')}
                      data-testid="copy-brand-signals"
                    >
                      {copiedSnippet === 'brandSignals' ? (
                        <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      Copy
                    </Button>
                  </div>
                  <pre className="bg-[#1E293B] p-4 rounded-lg overflow-x-auto text-sm text-gray-300 font-mono">
                    {brandSignalsCurl}
                  </pre>
                </div>

                {/* Product Signals */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-white font-medium">Product Signals Example</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(productSignalsCurl, 'productSignals')}
                      data-testid="copy-product-signals"
                    >
                      {copiedSnippet === 'productSignals' ? (
                        <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      Copy
                    </Button>
                  </div>
                  <pre className="bg-[#1E293B] p-4 rounded-lg overflow-x-auto text-sm text-gray-300 font-mono">
                    {productSignalsCurl}
                  </pre>
                </div>

                {/* Response info */}
                <div className="bg-[#1E293B]/50 p-4 rounded-lg border border-[#334155]">
                  <p className="text-gray-400 text-sm">
                    These endpoints return views, purchases, conversion, trends, and activity tiers
                    (high/medium/low) for your brand and products.
                  </p>
                </div>

                {/* Link to docs */}
                <div className="flex items-center gap-4 pt-4 border-t border-[#1E3A5F]">
                  <a 
                    href="/docs/signals" 
                    className="text-blue-400 hover:text-blue-300 flex items-center gap-2"
                  >
                    <BookOpen className="w-4 h-4" />
                    Full Signals API Documentation
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create API Key Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#0D1526] border-[#1E3A5F] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Create API Key</DialogTitle>
            <DialogDescription className="text-gray-400">
              We will generate a new API key for this brand.
              You will see the full key only once – make sure to copy it and store it securely.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="key-name" className="text-gray-300">Key Name</Label>
              <Input
                id="key-name"
                placeholder="e.g. Shopify App, Backend Server, Data Team"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                className="bg-[#1E293B] border-[#334155] text-white mt-1"
                data-testid="key-name-input"
              />
            </div>
            <div>
              <Label htmlFor="key-description" className="text-gray-300">Description (optional)</Label>
              <Input
                id="key-description"
                placeholder="This key is for our internal data warehouse"
                value={newKeyDescription}
                onChange={(e) => setNewKeyDescription(e.target.value)}
                className="bg-[#1E293B] border-[#334155] text-white mt-1"
                data-testid="key-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateModal(false)}
              className="border-[#334155] text-gray-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateKey}
              disabled={creating}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-create-key"
            >
              {creating ? 'Creating...' : 'Create Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show New Key Modal */}
      <Dialog open={showKeyModal} onOpenChange={setShowKeyModal}>
        <DialogContent className="bg-[#0D1526] border-[#1E3A5F] text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              API Key Created
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-yellow-400 font-medium">Copy this key now!</p>
                <p className="text-yellow-400/80 text-sm">You won&apos;t be able to see it again.</p>
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Your API Key</Label>
              <div className="flex items-center gap-2 mt-2">
                <code className="flex-1 bg-[#1E293B] p-3 rounded font-mono text-sm text-green-400 break-all">
                  {newKey?.api_key}
                </code>
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(newKey?.api_key, 'new-key')}
                  className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                  data-testid="copy-new-key"
                >
                  {copiedSnippet === 'new-key' ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <Label className="text-gray-500">Name</Label>
                <p className="text-white">{newKey?.name}</p>
              </div>
              <div>
                <Label className="text-gray-500">Status</Label>
                <Badge className="bg-green-500/10 text-green-400 border-green-500/30">
                  {newKey?.status}
                </Badge>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowKeyModal(false)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              I&apos;ve copied my key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
